import React from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    TextField,
    InputAdornment,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Collapse,
} from '@mui/material';
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Refresh,
    Save,
    FolderOpen,
    Search,
    Clear,
} from '@mui/icons-material';

const ParameterList = ({
    selectedMavId,
    params,
    displayParams,
    openGroups,
    searchQuery,
    onSearchChange,
    handleGroupClick,
    handleRowClick,
    handleRefreshClick,
}) => {
    const paramNames = Object.keys(displayParams).sort((a, b) => a.localeCompare(b));
    const searching = searchQuery !== '';

    const handleLoadParamsFromFile = () => window.api.send('load-params-from-file', selectedMavId);
    const handleSaveParamsToFile = () => window.api.send('save-params-to-file', selectedMavId);

    return (
        <Paper
            variant="outlined"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
            {/* Header: search + actions */}
            <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{
                    px: 1,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <TextField
                    placeholder="Search parameters"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searching ? (
                            <InputAdornment position="end">
                                <IconButton onClick={() => onSearchChange('')} edge="end">
                                    <Clear fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Tooltip title="Reload parameters from vehicle">
                    <IconButton onClick={handleRefreshClick}>
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Load parameters from file">
                    <IconButton onClick={handleLoadParamsFromFile}>
                        <FolderOpen fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Save current parameters to file">
                    <IconButton onClick={handleSaveParamsToFile}>
                        <Save fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* List */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableBody>
                        {paramNames.map((group) => {
                            const groupParams = displayParams[group];
                            if (!groupParams || Object.keys(groupParams).length === 0) return null;
                            const open = openGroups[group] || searching;
                            return (
                                <React.Fragment key={group}>
                                    <TableRow
                                        hover
                                        onClick={() => handleGroupClick(group)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        <TableCell
                                            colSpan={2}
                                            sx={{
                                                fontWeight: 600,
                                                py: 0.5,
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {open ? (
                                                    <KeyboardArrowUp fontSize="small" />
                                                ) : (
                                                    <KeyboardArrowDown fontSize="small" />
                                                )}
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {group}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    ({Object.keys(groupParams).length})
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ p: 0, border: 0 }} colSpan={2}>
                                            <Collapse in={open} timeout="auto" unmountOnExit>
                                                <Table size="small">
                                                    <TableBody>
                                                        {Object.keys(groupParams).map((paramName) => (
                                                            <TableRow
                                                                key={paramName}
                                                                hover
                                                                onClick={() => handleRowClick(paramName)}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{ fontFamily: 'monospace', pl: 3 }}
                                                                >
                                                                    {paramName}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                                                                >
                                                                    {params[paramName] ? params[paramName].value : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    );
};

export default ParameterList;
